import React, { useState, useEffect } from 'react';

/**
 * Year-by-Year Varsaphala Display Component
 * Shows Varsaphala (Solar Return) for any selected year like Parashara Light
 */
const VarsaphalaYearByYear = ({ natalData }) => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [varsaphalaData, setVarsaphalaData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Generate year options (from birth year to 50 years in future)
  const birthYear = natalData?.birth_date ? parseInt(natalData.birth_date.split('-')[0]) : 1997;
  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let y = birthYear; y <= currentYear + 50; y++) {
    yearOptions.push(y);
  }

  // Load Varsaphala for selected year
  useEffect(() => {
    if (natalData && selectedYear) {
      loadVarsaphala();
    }
  }, [selectedYear]);

  const loadVarsaphala = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';
      const response = await fetch(`${backendUrl}/api/varsaphala/single-year`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          person_id: natalData.person_id || 'temp',
          natal_sun_lon: natalData.sun_lon,
          natal_asc_sign_idx: natalData.asc_sign_idx,
          natal_date: natalData.birth_date,
          target_year: selectedYear,
          lat: natalData.latitude,
          lon: natalData.longitude
        })
      });

      if (!response.ok) throw new Error('Failed to load Varsaphala');
      
      const data = await response.json();
      setVarsaphalaData(data.varsaphala);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!natalData) {
    return <div className="p-4 bg-gray-50 rounded">No natal data available</div>;
  }

  // Extract nested data
  const vl = varsaphalaData?.varsha_lagna?.varsha_lagna || varsaphalaData?.varsha_lagna || {};
  const planets = varsaphalaData?.varsha_lagna?.varsha_planets || {};
  const solarReturnDate = varsaphalaData?.varsha_lagna?.solar_return_datetime || '';
  const muntha = varsaphalaData?.muntha || {};
  const varshesa = varsaphalaData?.varshesa || {};
  const ageCompleted = selectedYear - birthYear;

  // Format date
  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    const d = new Date(isoString);
    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // Planet colors for display
  const planetColors = {
    'Sun': 'bg-orange-100 text-orange-700 border-orange-300',
    'Moon': 'bg-blue-100 text-blue-700 border-blue-300',
    'Mars': 'bg-red-100 text-red-700 border-red-300',
    'Mercury': 'bg-green-100 text-green-700 border-green-300',
    'Jupiter': 'bg-yellow-100 text-yellow-700 border-yellow-300',
    'Venus': 'bg-pink-100 text-pink-700 border-pink-300',
    'Saturn': 'bg-gray-100 text-gray-700 border-gray-300'
  };

  return (
    <div className="space-y-6">
      {/* Year Selector Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-600 p-6 rounded-xl text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-3xl">☀️</span>
              <h2 className="text-2xl font-bold">Varsaphala - Year by Year</h2>
            </div>
            <p className="text-amber-100">Select any year to see solar return chart</p>
          </div>
          
          {/* Year Selector */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSelectedYear(Math.max(birthYear, selectedYear - 1))}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all"
              disabled={selectedYear <= birthYear}
            >
              ← Prev
            </button>
            
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-4 py-2 bg-white text-gray-800 rounded-lg font-semibold text-lg cursor-pointer"
            >
              {yearOptions.map(year => (
                <option key={year} value={year}>
                  {year} (Age {year - birthYear})
                </option>
              ))}
            </select>
            
            <button
              onClick={() => setSelectedYear(Math.min(currentYear + 50, selectedYear + 1))}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all"
              disabled={selectedYear >= currentYear + 50}
            >
              Next →
            </button>
          </div>
        </div>
      </div>

      {/* Loading/Error States */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
          <p className="mt-4 text-gray-600">Loading Varsaphala for {selectedYear}...</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          Error: {error}
        </div>
      )}

      {/* Varsaphala Data Display */}
      {!loading && !error && varsaphalaData && (
        <>
          {/* Completed Year Header - Like Parashara Light */}
          <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-amber-200">
            <div className="text-center">
              <div className="text-sm text-gray-500 mb-2">Completed</div>
              <div className="text-4xl font-bold text-amber-600 mb-2">
                {ageCompleted} Year{ageCompleted !== 1 ? 's' : ''}
              </div>
              <div className="text-lg text-gray-700">
                Solar Return: <span className="font-semibold">{formatDate(solarReturnDate)}</span>
              </div>
            </div>
          </div>

          {/* Main Chart Info Grid - Like Parashara Light Layout */}
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {/* Varsha Lagna (As) */}
              <div className="text-center p-4 bg-indigo-50 rounded-lg border-2 border-indigo-200">
                <div className="text-xs text-gray-600 mb-1">As (Varsha Lagna)</div>
                <div className="text-lg font-bold text-indigo-700">{vl.sign || 'N/A'}</div>
                <div className="text-sm text-gray-600">{vl.degree_in_sign?.toFixed(2)}°</div>
              </div>

              {/* Year Lord (YL) */}
              <div className="text-center p-4 bg-purple-50 rounded-lg border-2 border-purple-200">
                <div className="text-xs text-gray-600 mb-1">YL (Year Lord)</div>
                <div className="text-lg font-bold text-purple-700">{varshesa.year_lord || 'N/A'}</div>
              </div>

              {/* Muntha (Mun) */}
              <div className="text-center p-4 bg-pink-50 rounded-lg border-2 border-pink-200">
                <div className="text-xs text-gray-600 mb-1">Mun (Muntha)</div>
                <div className="text-lg font-bold text-pink-700">{muntha.sign || 'N/A'}</div>
                <div className="text-xs text-gray-500">House {muntha.house_from_lagna || 'N/A'}</div>
              </div>

              {/* Munthesha */}
              <div className="text-center p-4 bg-rose-50 rounded-lg border-2 border-rose-200">
                <div className="text-xs text-gray-600 mb-1">Munthesha</div>
                <div className="text-lg font-bold text-rose-700">{muntha.munthesha || 'N/A'}</div>
              </div>
            </div>

            {/* Planetary Positions - Like Parashara Light */}
            <div className="border-t-2 border-gray-200 pt-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Planetary Positions at Solar Return</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {Object.entries(planets).map(([planet, data]) => (
                  <div
                    key={planet}
                    className={`p-4 rounded-lg border-2 ${planetColors[planet] || 'bg-gray-50 text-gray-700 border-gray-200'}`}
                  >
                    <div className="font-bold text-sm mb-2">{planet}</div>
                    <div className="space-y-1 text-sm">
                      <div>
                        <span className="font-semibold">{data.sign}</span>
                        {data.retrograde && <span className="ml-1 text-red-600">(R)</span>}
                      </div>
                      <div className="text-xs">
                        {data.longitude?.toFixed(2)}° ({(data.longitude % 30)?.toFixed(2)}° in sign)
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Additional Details - Collapsible Sections */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Sahamas */}
            {varsaphalaData.sahamas && Object.keys(varsaphalaData.sahamas).length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-lg font-bold text-gray-800 mb-4">🌟 Sahamas (Arabic Parts)</h3>
                <div className="space-y-2">
                  {Object.entries(varsaphalaData.sahamas).slice(0, 5).map(([name, data]) => (
                    <div key={name} className="flex justify-between p-2 bg-purple-50 rounded">
                      <span className="font-medium capitalize">{name.replace('_', ' ')}</span>
                      <span className="text-purple-700">{data.sign} {data.longitude?.toFixed(2)}°</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Mudda Dasha Preview */}
            {varsaphalaData.mudda_dasha && varsaphalaData.mudda_dasha.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-lg font-bold text-gray-800 mb-4">📅 Mudda Dasha (First 6 Months)</h3>
                <div className="space-y-2">
                  {varsaphalaData.mudda_dasha.slice(0, 6).map((period, idx) => (
                    <div key={idx} className="flex justify-between p-2 bg-cyan-50 rounded text-sm">
                      <span>Month {period.month}: <span className="font-semibold text-cyan-700">{period.planet}</span></span>
                      <span className="text-gray-600">{period.start_date}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default VarsaphalaYearByYear;
